import { Component, EventEmitter, HostListener, Input, OnChanges, SimpleChanges, Output, ViewChild } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { PropertyField } from '../../../../shared/components/property-panel/property-panel.model';
import { ReportsService } from '../../../reports.service';
import { GlobalFilterColumnOption, GlobalFilterCondition } from '../global-filter-modal/global-filter-modal.component';

export type FilterControlType = 'hierarchal' | 'hierarchal-delimiter' | 'single-select' | 'multiselect';

export interface FilterProperties {
  defaultSelection: string[];
  visibility: boolean;
  searchType: 'Equal' | 'Like';
  width: number;
}

export interface FilterRow {
  id: string;
  name: string;
  type: FilterControlType | '';
  selectedColumns: string[];
  properties: FilterProperties;
  columnPickerOpen?: boolean;
  columnSearch?: string;
  expandedGroups?: { [group: string]: boolean };
}

// Multiple column selection is only offered for the Hierarchal control type.
export function allowsMultipleColumns(row: FilterRow): boolean {
  return row.type === 'hierarchal';
}

interface DataColumnOption {
  key: string;
  name: string;
  type?: string;
  isPrimaryKey?: boolean;
  tableName: string;
  columnName: string;
}

interface DataColumnGroup {
  group: string;
  columns: DataColumnOption[];
}

function createDefaultProperties(): FilterProperties {
  return { defaultSelection: ['All'], visibility: true, searchType: 'Like', width: 3 };
}

export function isFilterRowValid(row: FilterRow): boolean {
  return !!row.name.trim() && !!row.type && row.selectedColumns.length > 0;
}

export function hasDuplicateFilterNames(rows: FilterRow[]): boolean {
  const seen = new Set<string>();
  for (const row of rows) {
    const key = row.name.trim().toLowerCase();
    if (!key) { continue; }
    if (seen.has(key)) { return true; }
    seen.add(key);
  }
  return false;
}

/**
 * "Visual Editor" tab of the Add filter popup - a reorderable table of filter rows (name, control
 * type, data column picker + per-row "manage properties" panel), matching the required designer UI.
 */
@Component({
  selector: 'app-add-filter',
  standalone: false,
  templateUrl: './add-filter.component.html',
  styleUrl: './add-filter.component.scss'
})
export class AddFilterComponent implements OnChanges {
  @Input() draft: FilterRow[] | null = null;
  @Output() draftChange = new EventEmitter<FilterRow[]>();
  // Report's datasource config (object, or JSON string) - { datasets: [{ id, name, table_name, columns: [...] }] }.
  @Input() selectedDataSource: any = null;

  constructor(private reportsService: ReportsService) {}

  readonly controlTypeOptions: Array<{ value: FilterControlType; label: string }> = [
    { value: 'hierarchal', label: 'Hierarchal' },
    { value: 'hierarchal-delimiter', label: 'Hierarchal-delimiter' },
    { value: 'single-select', label: 'Single Select' },
    { value: 'multiselect', label: 'Multiselect' }
  ];

  readonly defaultSelectionOptions = ['All'];
  readonly searchTypeOptions = [
    { value: 'Equal', label: 'Equal' },
    { value: 'Like', label: 'Like' }
  ];

  rows: FilterRow[] = [];
  columnGroups: DataColumnGroup[] = [];
  activePropertyRowId: string | null = null;

  private idCounter = 0;
  // `draftChange` round-trips through the parent back into `[draft]` - ignore that echo so we don't
  // replace `rows` (and break drag reordering / open property panel lookups) on every keystroke.
  private suppressNextDraftSync = false;
  defaultSelectionValuesLoading = false;
  private defaultSelectionApiValues: string[] = [];

  // Closes the column picker when clicking anywhere outside the "Selected data column" cell.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.add-filter__col--column')) {
      return;
    }
    this.rows.forEach(row => row.columnPickerOpen = false);
  }

  globalFilterConditions: GlobalFilterCondition[] = [];
  // Cached (not a getter) - a new array identity every CD cycle made the column mat-select's *ngFor
  // destroy/recreate its options every check and froze the page, same as the propertyFields bug.
  globalFilterColumnOptions: GlobalFilterColumnOption[] = [];
  @ViewChild('globalFilterModal') private globalFilterModal: any;
  @ViewChild('globalFilterModalBody') private globalFilterModalBody: any;

  onOpenGlobalFilter(): void {
    this.globalFilterModalBody?.resetState(this.globalFilterConditions);
    this.globalFilterModal?.show();
  }

  onGlobalFilterCancelled(): void {
    this.globalFilterModal?.hide();
  }

  onGlobalFilterSaved(conditions: GlobalFilterCondition[]): void {
    this.globalFilterConditions = conditions;
    this.globalFilterModal?.hide();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['draft']) {
      if (this.suppressNextDraftSync) {
        this.suppressNextDraftSync = false;
      } else {
        this.rows = this.draft?.length ? this.draft.map(row => ({ ...row })) : [this.createEmptyRow()];
      }
    }
    if (changes['selectedDataSource']) {
      this.columnGroups = this.buildColumnGroups();
      this.globalFilterColumnOptions = this.columnGroups.flatMap(group => group.columns.map(col => ({
        key: col.key, label: col.name, tableName: col.tableName, columnName: col.columnName
      })));
    }
  }

  get activeRow(): FilterRow | undefined {
    return this.rows.find(row => row.id === this.activePropertyRowId);
  }

  // Cached (not a getter) - rebuilding this array/its nested option arrays on every CD cycle made the
  // property panel's *ngFor destroy/recreate its mat-select every check and froze the page.
  propertyFields: PropertyField[] = [];

  private buildPropertyFields(row: FilterRow): PropertyField[] {
    const allowsMultipleDefaults = row.type === 'multiselect' || row.type === 'hierarchal' || row.type === 'hierarchal-delimiter';
    const canReorder = allowsMultipleColumns(row) && row.selectedColumns.length > 1;
    const defaultSelectionValues = [...this.defaultSelectionOptions, ...this.defaultSelectionApiValues];
    const fields: PropertyField[] = [
      {
        key: 'defaultSelection',
        label: this.defaultSelectionValuesLoading ? 'Default selection (loading values...)' : 'Default selection',
        type: 'select', multiple: allowsMultipleDefaults,
        options: defaultSelectionValues.map(option => ({ value: option, label: option }))
      },
      { key: 'visibility', label: 'Visibility', type: 'toggle' },
      { key: 'searchType', label: 'Search', type: 'radio-toggle', options: this.searchTypeOptions },
      { key: 'width', label: 'Width', type: 'slider', min: 3, max: 12, color: '#013b71' }
    ];
    if (row.selectedColumns.length) {
      fields.push({
        key: 'selectedColumnsOrder',
        label: canReorder ? 'Selected columns (drag to set hierarchy order)' : 'Selected columns',
        type: 'reorder-list',
        options: row.selectedColumns.map(key => ({ value: key, label: this.getColumnLabel(key) })),
        reorderable: canReorder
      });
    }
    return fields;
  }

  get activePropertyValues(): { [key: string]: any } {
    const row = this.activeRow;
    return row ? { ...row.properties, selectedColumnsOrder: row.selectedColumns } : {};
  }

  get summaryText(): string {
    const count = this.rows.length;
    return `${count} filter${count === 1 ? '' : 's'}`;
  }

  trackByRowId(_index: number, row: FilterRow): string {
    return row.id;
  }

  isDuplicateName(row: FilterRow): boolean {
    const name = row.name.trim().toLowerCase();
    if (!name) { return false; }
    return this.rows.some(other => other !== row && other.name.trim().toLowerCase() === name);
  }

  addRow(): void {
    this.rows = [...this.rows, this.createEmptyRow()];
    this.onFieldChange();
  }

  removeRow(row: FilterRow): void {
    this.rows = this.rows.filter(r => r !== row);
    if (this.activePropertyRowId === row.id) { this.activePropertyRowId = null; }
    this.onFieldChange();
  }

  onDrop(event: CdkDragDrop<FilterRow[]>): void {
    moveItemInArray(this.rows, event.previousIndex, event.currentIndex);
    this.onFieldChange();
  }

  // Switching away from Hierarchal trims back down to a single selected column.
  onTypeChange(row: FilterRow): void {
    if (!allowsMultipleColumns(row) && row.selectedColumns.length > 1) {
      row.selectedColumns = row.selectedColumns.slice(0, 1);
    }
    this.onFieldChange();
  }

  toggleColumnPicker(row: FilterRow): void {
    const wasOpen = !!row.columnPickerOpen;
    this.rows.forEach(r => r.columnPickerOpen = false);
    row.columnPickerOpen = !wasOpen;
  }

  toggleGroup(row: FilterRow, groupName: string): void {
    row.expandedGroups = row.expandedGroups || {};
    row.expandedGroups[groupName] = row.expandedGroups[groupName] === false;
  }

  isGroupExpanded(row: FilterRow, groupName: string): boolean {
    return !row.expandedGroups || row.expandedGroups[groupName] !== false;
  }

  filteredGroups(row: FilterRow): DataColumnGroup[] {
    const term = (row.columnSearch || '').trim().toLowerCase();
    if (!term) { return this.columnGroups; }
    return this.columnGroups
      .map(group => ({ group: group.group, columns: group.columns.filter(col => col.name.toLowerCase().includes(term)) }))
      .filter(group => group.columns.length > 0);
  }

  isColumnSelected(row: FilterRow, col: DataColumnOption): boolean {
    return row.selectedColumns.includes(col.key);
  }

  onColumnToggle(row: FilterRow, col: DataColumnOption): void {
    if (allowsMultipleColumns(row)) {
      const idx = row.selectedColumns.indexOf(col.key);
      if (idx >= 0) { row.selectedColumns.splice(idx, 1); } else { row.selectedColumns.push(col.key); }
    } else {
      row.selectedColumns = [col.key];
      row.columnPickerOpen = false;
    }
    this.onFieldChange();
  }

  // Template-facing wrapper around the exported `allowsMultipleColumns()` helper.
  canSelectMultipleColumns(row: FilterRow): boolean {
    return allowsMultipleColumns(row);
  }

  selectedColumnsLabel(row: FilterRow): string {
    if (!row.selectedColumns.length) { return 'Select data column'; }
    return row.selectedColumns.map(key => this.getColumnLabel(key)).join(', ');
  }

  // "{type} • Unique" meta shown next to a column's name in the picker, when available.
  columnMeta(col: DataColumnOption): string {
    return [col.type, col.isPrimaryKey ? 'Unique' : ''].filter(Boolean).join(' \u2022 ');
  }

  openProperties(row: FilterRow): void {
    this.rows.forEach(r => r.columnPickerOpen = false);
    this.activePropertyRowId = row.id;
    this.defaultSelectionApiValues = [];
    this.propertyFields = this.buildPropertyFields(row);
    this.fetchDefaultSelectionValues(row);
  }

  closeProperties(): void {
    this.activePropertyRowId = null;
    this.propertyFields = [];
  }

  onPropertyValueChange(event: { key: string; value: any }): void {
    const row = this.activeRow;
    if (!row) { return; }
    if (event.key === 'selectedColumnsOrder') {
      row.selectedColumns = [...event.value];
    } else {
      (row.properties as any)[event.key] = event.value;
    }
    this.onFieldChange();
  }

  onFieldChange(): void {
    // Keep the open panel's "Default selection" multiple-flag in sync if the row's Type just changed.
    if (this.activeRow) {
      this.propertyFields = this.buildPropertyFields(this.activeRow);
    }
    this.suppressNextDraftSync = true;
    this.draftChange.emit(this.rows.map(row => ({ ...row })));
  }

  private createEmptyRow(): FilterRow {
    return {
      id: `filter-${Date.now()}-${this.idCounter++}`,
      name: '',
      type: '',
      selectedColumns: [],
      properties: createDefaultProperties(),
      expandedGroups: {}
    };
  }

  private getColumnLabel(key: string): string {
    for (const group of this.columnGroups) {
      const found = group.columns.find(col => col.key === key);
      if (found) { return found.name; }
    }
    return key;
  }

  private findColumnOption(key: string): DataColumnOption | undefined {
    for (const group of this.columnGroups) {
      const found = group.columns.find(col => col.key === key);
      if (found) { return found; }
    }
    return undefined;
  }

  // Populates `defaultSelectionApiValues` with the real distinct values for the row's selected column(s)
  // so "Default selection" can offer them alongside the fixed All/None options.
  private fetchDefaultSelectionValues(row: FilterRow): void {
    const columns = row.selectedColumns.map(key => this.findColumnOption(key)).filter((col): col is DataColumnOption => !!col);
    if (!columns.length) {
      return;
    }
    this.defaultSelectionValuesLoading = true;
    this.reportsService.getValuesByColumnNames({
      tableName: columns[0].tableName,
      columnNames: columns.map(col => col.columnName),
      distinct: true
    }).subscribe({
      next: (response: any) => {
        this.defaultSelectionValuesLoading = false;
        this.defaultSelectionApiValues = Array.isArray(response) ? response : (response?.values || []);
        if (this.activeRow === row) {
          this.propertyFields = this.buildPropertyFields(row);
        }
      },
      error: () => {
        this.defaultSelectionValuesLoading = false;
        this.defaultSelectionApiValues = [];
      }
    });
  }

  // One section per dataset the user configured (not grouped by column type) - each dataset's already
  // user-selected columns (from `selectedDataSource`) become that section's list.
  private buildColumnGroups(): DataColumnGroup[] {
    let parsed: any = this.selectedDataSource;
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch { parsed = null; }
    }
    const datasets: any[] = parsed?.datasets || [];
    return datasets.map(dataset => ({
      group: dataset.name || dataset.table_name || 'Other',
      columns: (dataset.columns || []).map((col: any) => ({
        key: `${dataset.id}.${col.key}`,
        name: col.name || col.key,
        type: col.type,
        isPrimaryKey: !!col.is_primary_key,
        tableName: dataset.table_name,
        columnName: col.key
      }))
    }));
  }
}
